import { useEffect, useState } from "react";
import '../styles/gameComponent.scss';
import { useUserStore } from "@/components/login/loginStore";
import SEO from '../components/SEO';
import { fetchData, postData } from "@/utils/requests";
import { useTranslation } from 'react-i18next';
import { Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { isMobile } from "@/stores/MenuStore.ts";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

type BotFilter = {
    id: number;
    filter_id: number;
    filter_name: string;
    filter_key: string;
    icon: string | null;
    filter_type: 'player' | 'team';
    at_least: number;
    in_last: number;
    hit_rate: number;
};

export default function TelegramBot() {

    const NAME_BOT = 'craquestats_bot';

    const { t } = useTranslation();
    const user = useUserStore((state) => state);
    const [telegramConfigured, setTelegramConfigured] = useState<boolean>(false);
    const [userFilters, setUserFilters] = useState<number[]>([]);
    const [botFilters, setBotFilters] = useState<BotFilter[]>([]);
    const [isUpdateUser, setIsUpdateUser] = useState<boolean>(false);
    const [showPremiumCTA, setShowPremiumCTA] = useState<boolean>(false);
    const [botUrl, setBotUrl] = useState(`https://t.me/${NAME_BOT}`);

    const navigate = useNavigate();

    const getBotUserInfo = () => {
        fetchData('/api/telegram/user', (data) => {
            setTelegramConfigured(data.telegram_configured);
            setUserFilters(Array.isArray(data.user_filters) ? data.user_filters : [])
            if (data.telegram_configured) {
                setIsUpdateUser(false);
            }
        });
    };

    useEffect(() => {
        if (!isUpdateUser || telegramConfigured) return;
        getBotUserInfo();
        const interval = setInterval(() => {
            getBotUserInfo();
        }, 10000);
        return () => clearInterval(interval);
    }, [isUpdateUser]);

    useEffect(() => {
        fetchData('/api/bot_filters', (data) => {
            setBotFilters(data);
        });
        if (user.logged) {
            getBotUserInfo();
            postData('/api/telegram/connect', {}, (data) => {
                setBotUrl(`https://t.me/${NAME_BOT}?start=${data.token}`);
            });
        };
    }, [user.logged]);

    const setFilters = (filter_id: number) => {
        const isDeselecting = userFilters.includes(filter_id);
        if (!user.premium && !isDeselecting && userFilters.length >= 1) {
            setShowPremiumCTA(true);
            return;
        }
        setShowPremiumCTA(false);
        postData('/api/bot_filters', { filter_id: filter_id }, (data) => {
            setUserFilters(Array.isArray(data) ? data : []);
            toast.success(t('telegram_bot.telegram_bot_11'));
        });
    };

    const buttonTelegram = (text: string, bgColor: string = 'bg-primary-500', textColor: string = 'text-black/80', pulse: boolean = false) => {
        return (
            <a
                href={botUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsUpdateUser(true)}
                className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg cursor-pointer ${textColor} ${bgColor} ${pulse ? 'btn-pulse' : ''}`}
            >
                <Icon
                    icon="bxl:telegram"
                    width={24}
                    className="flex-none"
                />
                <span className="font-semibold">
                    {text}
                </span>
            </a>
        );
    };

    const filterTypeDiv = (filterType: 'player' | 'team') => {
        return (
            <div className="flex w-full flex-col gap-3">
                <span className="flex text-lg font-semibold">{filterType == 'player' ? t('telegram_bot.telegram_bot_7') : t('telegram_bot.telegram_bot_8')}</span>
                <div className="flex w-full flex-wrap max-w-lg gap-3">
                    {botFilters.filter(f => f.filter_type == filterType).map(f => {
                        const filterColor = userFilters.includes(f.filter_id) ? 'border-primary-500' : 'border-default-50';
                        return (
                            <Button key={`${f.filter_id}-${filterType}`} onClick={() => setFilters(f.filter_id)}
                                className={`flex items-center border transition text-default-900 bg-default-50 ${filterColor}`}>
                                <div className="flex items-center gap-2">
                                    <Icon className="text-lg" icon={f.icon ? f.icon : ''} />
                                    <span className="font-semibold">{t(f.filter_name)}</span>
                                </div>
                            </Button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
            <SEO
                title={`${t('telegram_bot.telegram_bot_1')} | Craque Stats`}
                description={t('telegram_bot.telegram_bot_2')}
            />

            <div className="flex flex-col w-full gap-6">
                <div className="flex flex-col w-full gap-3">
                    <h1 className="text-2xl font-bold">{t('telegram_bot.telegram_bot_1')}</h1>
                    <p className="text-sm text-default-500">
                        {t('telegram_bot.telegram_bot_2')}
                    </p>
                    <div className="flex items-center gap-2 w-fit px-3 py-1.5 rounded-full bg-primary-500/15 border border-primary-500/40">
                        <Icon icon="bxl:telegram" className="text-primary-500 text-base flex-none" />
                        <span className="text-xs font-bold text-primary-500 tracking-wide uppercase">
                            {t('telegram_bot.telegram_bot_12')}
                        </span>
                    </div>
                </div>
                <div className={`flex items-center w-full ${isMobile() && 'justify-center'}`}>
                    {
                        !user.logged ? (
                            <div className="flex flex-col gap-2 bg-default-50 border border-primary-500 text-default-900 p-4 rounded-lg fadeIn">
                                <span className="text-sm font-semibold">
                                    {t('telegram_bot.telegram_bot_9')}
                                </span>
                                <div className="flex w-full">
                                    <Button onClick={() => navigate('/login?redirectAfterLogin=/telegram-bot')}
                                        className="flex items-center transition text-black/80 bg-primary-400 btn-pulse">
                                        <div className="flex items-center gap-2">
                                            <Icon className="text-lg" icon='material-symbols:login' />
                                            <span className="font-semibold">{'Login'}</span>
                                        </div>
                                    </Button>
                                </div>
                            </div>
                        ) : telegramConfigured ? (
                            <div className="flex flex-col gap-2 bg-primary-400 text-default-100 p-4 rounded-lg fadeIn">
                                <div className="flex w-full gap-3 items-center">
                                    <div className="flex w-[24px]">
                                        <Icon icon="fluent:bot-sparkle-32-filled" className="text-2xl" />
                                    </div>
                                    <span className="text-sm font-semibold">
                                        {t('telegram_bot.telegram_bot_3')}
                                    </span>
                                </div>
                                {!user.premium && <span className="text-sm font-semibold">{t('telegram_bot.telegram_bot_10')}</span>}
                                <div className="flex w-full gap-3">
                                    {buttonTelegram(t('telegram_bot.telegram_bot_4'), 'bg-default-100', 'text-default-900')}
                                    {!user.premium &&
                                        <Button onClick={() => navigate('/plans')}
                                            className="flex items-center transition text-default-900 bg-default-100">
                                            <div className="flex items-center gap-2">
                                                <Icon className="text-2xl" icon='mdi:crown' />
                                                <span className="font-semibold">{'Premium'}</span>
                                            </div>
                                        </Button>
                                    }
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-2 bg-default-50 border border-primary-500 text-default-900 p-4 rounded-lg fadeIn">
                                <span className="text-sm font-semibold">
                                    {t('telegram_bot.telegram_bot_5')}
                                </span>
                                <div className="flex w-full">
                                    {buttonTelegram(t('telegram_bot.telegram_bot_6'), 'bg-primary-500', 'text-black/80', true)}
                                </div>
                            </div>
                        )
                    }
                </div>
                {showPremiumCTA && (
                    <div className="flex items-center justify-between gap-4 bg-content1 border border-primary-500/60 rounded-xl p-4 max-w-lg fadeIn">
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <Icon icon="mdi:crown" className="text-primary-500 text-lg flex-none" />
                                <span className="font-bold text-sm text-default-900">
                                    {t('telegram_bot.telegram_bot_13')}
                                </span>
                            </div>
                            <p className="text-xs text-default-500 leading-relaxed">
                                {t('telegram_bot.telegram_bot_14')}
                            </p>
                        </div>
                        <Button
                            size="sm"
                            className="flex-none bg-primary-500 text-black/80 font-bold"
                            onClick={() => navigate('/plans')}
                        >
                            <Icon icon="mdi:crown" className="text-base" />
                            {t('telegram_bot.telegram_bot_15')}
                        </Button>
                    </div>
                )}
                <div className={`flex flex-col gap-6 transition-opacity duration-300 ${(!user.logged || !telegramConfigured) ? 'opacity-60 pointer-events-none select-none' : ''}`}>
                    {filterTypeDiv('player')}
                    {filterTypeDiv('team')}
                </div>
            </div>
        </>
    );
};
